import { Button } from "@/components/ui/button";
import { GithubProjectEntity, GithubRepoEntity } from "@cohesive/api-entities";
import { ChevronLeft, FolderGit2, Kanban } from "lucide-react";
import React from "react";

type IssueState = "all" | "open" | "closed";
type ItemType = "issues" | "prs";

interface ViewHeaderProps {
  selectedRepo?: GithubRepoEntity | null;
  selectedProject?: GithubProjectEntity | null;
  onBack: () => void;
  // Filter props for repo items
  itemType?: ItemType;
  onItemTypeChange?: (type: ItemType) => void;
  // Filter props for both
  issueState?: IssueState;
  onIssueStateChange?: (state: IssueState) => void;
}

const StateFilters: React.FC<{
  issueState: IssueState;
  onIssueStateChange: (state: IssueState) => void;
}> = ({ issueState, onIssueStateChange }) => (
  <div className="flex gap-0.5">
    {(["all", "open", "closed"] as const).map((state) => (
      <Button
        key={state}
        variant={issueState === state ? "secondary" : "ghost"}
        size="sm"
        onClick={() => onIssueStateChange(state)}
        className="h-6 px-2 text-xs capitalize"
      >
        {state}
      </Button>
    ))}
  </div>
);

export const ViewHeader: React.FC<ViewHeaderProps> = ({
  selectedRepo,
  selectedProject,
  onBack,
  itemType,
  onItemTypeChange,
  issueState,
  onIssueStateChange,
}) => {
  if (selectedRepo) {
    return (
      <div className="sticky top-0 z-10 w-full flex items-center justify-between gap-2 px-3 py-2 border-b bg-background/95 backdrop-blur-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="h-7 px-2 flex-shrink-0"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          <FolderGit2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="font-medium text-sm truncate">
            {selectedRepo.data.full_name}
          </span>
        </div>
        {itemType && onItemTypeChange && issueState && onIssueStateChange && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex gap-0.5">
              <Button
                variant={itemType === "issues" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => onItemTypeChange("issues")}
                className="h-6 px-2 text-xs"
              >
                Issues
              </Button>
              <Button
                variant={itemType === "prs" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => onItemTypeChange("prs")}
                className="h-6 px-2 text-xs"
              >
                PRs
              </Button>
            </div>
            <div className="h-4 w-px bg-border" />
            <StateFilters
              issueState={issueState}
              onIssueStateChange={onIssueStateChange}
            />
          </div>
        )}
      </div>
    );
  }

  if (selectedProject) {
    return (
      <div className="sticky top-0 z-10 w-full flex items-center justify-between gap-2 px-3 py-2 border-b bg-background/95 backdrop-blur-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="h-7 px-2 flex-shrink-0"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          <Kanban className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="font-medium text-sm truncate">
            {selectedProject.data.title}
          </span>
        </div>
        {issueState && onIssueStateChange && (
          <StateFilters
            issueState={issueState}
            onIssueStateChange={onIssueStateChange}
          />
        )}
      </div>
    );
  }

  return null;
};
